import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest, ApiRequestError } from '../api/client.js';
import type { WorkOrderDetail } from '../api/types.js';
import { useSession } from '../auth/SessionProvider.js';
import { PERMISSIONS } from '../../shared/permissions.js';
import {
  Alert,
  Button,
  Card,
  DefinitionList,
  Field,
  TextArea,
  TextInput,
} from '../components/ui.js';
import { formatDateTime } from '../lib/format.js';

/**
 * Marks work that was performed before a proposal could be priced, so the USPS
 * approver knows the proposal ratifies cost already incurred. VGS records who
 * authorized the call-out; the flag waives the three-quote floor but not approval.
 */
export function EmergencyCalloutPanel({ workOrder }: { workOrder: WorkOrderDetail }) {
  const queryClient = useQueryClient();
  const { can, isContractor } = useSession();
  const mayEdit = isContractor && can(PERMISSIONS.WORK_ORDER_EDIT) && workOrder.status !== 'closed';
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    emergencyCallout: workOrder.emergencyCallout,
    emergencyAuthorizedBy: workOrder.emergencyAuthorizedBy ?? '',
    emergencyAuthorizedAt: workOrder.emergencyAuthorizedAt
      ? toLocalInput(workOrder.emergencyAuthorizedAt)
      : '',
    emergencyJustification: workOrder.emergencyJustification ?? '',
  });

  const save = useMutation({
    mutationFn: () =>
      apiRequest(`/api/work-orders/${workOrder.id}`, {
        method: 'PATCH',
        body: {
          emergencyCallout: form.emergencyCallout,
          emergencyAuthorizedBy: form.emergencyCallout
            ? form.emergencyAuthorizedBy.trim() || null
            : null,
          emergencyAuthorizedAt:
            form.emergencyCallout && form.emergencyAuthorizedAt
              ? new Date(form.emergencyAuthorizedAt).toISOString()
              : null,
          emergencyJustification: form.emergencyCallout
            ? form.emergencyJustification.trim() || null
            : null,
          expectedUpdatedAt: workOrder.updatedAt,
        },
      }),
    onSuccess: () => {
      setEditing(false);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['work-order', workOrder.id] });
      void queryClient.invalidateQueries({ queryKey: ['pre-approval', workOrder.id] });
      void queryClient.invalidateQueries({ queryKey: ['timeline', workOrder.id] });
    },
    onError: (caught: unknown) =>
      setError(
        caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Try again.',
      ),
  });

  if (!workOrder.emergencyCallout && !mayEdit) return null;

  return (
    <Card title="Emergency call-out">
      {error && (
        <Alert variant="error" title="Could not save" className="mb-3">
          {error}
        </Alert>
      )}
      {editing ? (
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          <label className="flex items-start gap-2 text-sm text-slate-800">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={form.emergencyCallout}
              onChange={(event) =>
                setForm((previous) => ({ ...previous, emergencyCallout: event.target.checked }))
              }
            />
            <span>
              This was an emergency call-out — the work was performed before a proposal could be
              priced. USPS still approves the cost; the three-quote requirement is waived.
            </span>
          </label>
          {form.emergencyCallout && (
            <>
              <Field label="Call-out authorized by" htmlFor="emergencyAuthorizedBy" required>
                <TextInput
                  id="emergencyAuthorizedBy"
                  required
                  value={form.emergencyAuthorizedBy}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,
                      emergencyAuthorizedBy: event.target.value,
                    }))
                  }
                  placeholder="Name and role of the person who authorized the call-out"
                />
              </Field>
              <Field label="Authorized on" htmlFor="emergencyAuthorizedAt" required>
                <TextInput
                  id="emergencyAuthorizedAt"
                  type="datetime-local"
                  required
                  value={form.emergencyAuthorizedAt}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,
                      emergencyAuthorizedAt: event.target.value,
                    }))
                  }
                />
              </Field>
              <Field label="Why it could not wait" htmlFor="emergencyJustification" required>
                <TextArea
                  id="emergencyJustification"
                  rows={3}
                  required
                  value={form.emergencyJustification}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,
                      emergencyJustification: event.target.value,
                    }))
                  }
                  placeholder="What was failing, the risk to safety or operations, and who was called out"
                />
              </Field>
            </>
          )}
          <div className="flex gap-2">
            <Button type="submit" loading={save.isPending}>
              Save
            </Button>
            <Button type="button" variant="secondary" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <div className="flex flex-col gap-3">
          {workOrder.emergencyCallout ? (
            <>
              <Alert variant="warning" title="Work already performed">
                This proposal ratifies cost already incurred under an emergency call-out. The
                three-quote requirement does not apply; USPS approval is still required.
              </Alert>
              <DefinitionList
                items={[
                  { label: 'Authorized by', value: workOrder.emergencyAuthorizedBy },
                  {
                    label: 'Authorized on',
                    value: formatDateTime(workOrder.emergencyAuthorizedAt),
                  },
                  { label: 'Why it could not wait', value: workOrder.emergencyJustification },
                ]}
              />
            </>
          ) : (
            <p className="text-sm text-slate-600">
              Not an emergency call-out. If the work had to be done before a proposal could be
              priced, record that here so USPS sees it when approving.
            </p>
          )}
          {mayEdit && (
            <div>
              <Button variant="secondary" onClick={() => setEditing(true)}>
                {workOrder.emergencyCallout ? 'Edit' : 'Mark as emergency call-out'}
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function toLocalInput(iso: string): string {
  const date = new Date(iso);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
